// Merchant-attention count for weight disputes: OPEN + REVISED both need a response
// before the auto-accept window closes.
//
// NOTE: this is a deliberate frontend-side stopgap — it polls the existing WD summary
// endpoint. When the proper backend notification channel ships, this hook should be
// replaced by that feed (the consumers - sidebar badge + header bell - stay as-is).
import { useEffect, useState } from "react";
import { fetchSummary } from "../pages/weight-dispute/weightDisputeApi";

const TTL_MS = 5 * 60 * 1000;

let cache = { count: 0, at: 0 };
let inflight = null;
const listeners = new Set();
const notifyAll = () => listeners.forEach((fn) => fn(cache.count));

function load(force = false) {
  const token = localStorage.getItem("authToken");
  if (!token) return null;
  if (!force && Date.now() - cache.at < TTL_MS) {
    notifyAll();
    return null;
  }
  if (inflight) return inflight;
  inflight = fetchSummary(token)
    .then((summary) => {
      const counts = summary?.countsByStatus || {};
      cache = { count: (counts.OPEN ?? 0) + (counts.REVISED ?? 0), at: Date.now() };
      notifyAll();
    })
    .catch(() => {
      // Silent: an attention badge must never surface fetch errors.
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Returns the number of weight disputes needing merchant action.
 * Shared module cache: any number of consumers cause at most one fetch per TTL.
 * Pass enabled=false (e.g. no screen permission) to opt out entirely.
 */
export default function useWeightDisputeAttention(enabled = true) {
  const [count, setCount] = useState(cache.count);
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setCount);
    setCount(cache.count);
    load();
    const id = setInterval(() => load(true), TTL_MS);
    // Instant refresh when a dispute action lands (fired by weightDisputeApi).
    const onActionRefresh = () => load(true);
    window.addEventListener("wd:attention-refresh", onActionRefresh);
    return () => {
      listeners.delete(setCount);
      clearInterval(id);
      window.removeEventListener("wd:attention-refresh", onActionRefresh);
    };
  }, [enabled]);
  return enabled ? count : 0;
}
